import { Calendar as CalendarIcon, Clock, Check, ChevronRight } from "lucide-react";
import Button from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Calendar } from "@/components/ui/Calendar";
import type { SpecialistProfile } from "@/api/contracts/specialists.contract";
import type { AvailableTimeSlotsDto } from "@/api/contracts/appointment-slots.contract";
import { formatMonthDay } from "@/utils/date";
import { formatSlotTime } from "@/utils/appointments";

export const TELEMEDICINE_ID = "telemedicine";

interface BookingPanelProps {
  specialist: SpecialistProfile;
  offersTelemedicine: boolean;
  selectedLocationId: string | undefined;
  onLocationChange: (id: string) => void;
  isTelemedicine: boolean;
  selectedDate: Date | undefined;
  onDateSelect: (date: Date | undefined) => void;
  selectedSlot: AvailableTimeSlotsDto | undefined;
  onSlotSelect: (slot: AvailableTimeSlotsDto) => void;
  availableDates: string[];
  slots: AvailableTimeSlotsDto[];
  slotsLoading: boolean;
  onBookNow: () => void;
}

export function BookingPanel({
  specialist,
  offersTelemedicine,
  selectedLocationId,
  onLocationChange,
  isTelemedicine,
  selectedDate,
  onDateSelect,
  selectedSlot,
  onSlotSelect,
  availableDates,
  slots,
  slotsLoading,
  onBookNow,
}: BookingPanelProps) {
  const inPersonLocations = specialist.locationsInPerson ?? [];
  const totalOptions = inPersonLocations.length + (offersTelemedicine ? 1 : 0);
  const showLocationSelector = totalOptions >= 2;
  const hasAvailability = availableDates.length > 0 || slotsLoading;

  return (
    <Card className="overflow-hidden border-0 shadow-xl ring-1 ring-gray-200" id="booking-card">
      <div className="p-6 border-b border-gray-100">
        <h3 className="text-xl font-semibold text-gray-900 headline flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-gray-600" />
          Book Appointment
        </h3>
      </div>

      <div className="p-5 space-y-6 bg-white">
        {showLocationSelector && (
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Select Location</h4>
            <div className="space-y-2">
              {inPersonLocations.map((loc) => {
                if (!loc.id) return null;
                const id = loc.id;
                const isSelected = selectedLocationId === id;
                return (
                  <button
                    key={id}
                    onClick={() => onLocationChange(id)}
                    className={`w-full p-3 rounded-lg border text-left flex items-start gap-3 transition-colors ${
                      isSelected ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                      isSelected ? "border-primary bg-primary" : "border-gray-300"
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <div className="font-semibold text-base text-gray-900">{loc.name}</div>
                      <div className="text-sm text-gray-500 mt-0.5">{loc.address}, {loc.city}</div>
                    </div>
                  </button>
                );
              })}
              {offersTelemedicine && (
                <button
                  onClick={() => onLocationChange(TELEMEDICINE_ID)}
                  className={`w-full p-3 rounded-lg border text-left flex items-start gap-3 transition-colors ${
                    isTelemedicine ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                    isTelemedicine ? "border-primary bg-primary" : "border-gray-300"
                  }`}>
                    {isTelemedicine && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <div>
                      <div className="font-semibold text-base text-gray-900">Telemedicine</div>
                      <div className="text-sm text-gray-500 mt-0.5">Online video visit</div>
                    </div>
                  </div>
                </button>
              )}
            </div>
          </div>
        )}

        {hasAvailability ? (
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Select Date</h4>
            <Calendar
              selected={selectedDate}
              onSelect={onDateSelect}
              availableDates={availableDates}
              defaultMonth={availableDates[0] ? new Date(availableDates[0]) : new Date()}
              className="rounded-lg border border-gray-200 w-full p-3"
            />
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-20" />
            <p className="text-sm font-medium">No availability at this time</p>
            <p className="text-xs mt-1">Please check back later</p>
          </div>
        )}

        {selectedDate && (
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center justify-between">
              <span>Available Times</span>
              <span className="text-sm normal-case font-medium text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                {formatMonthDay(selectedDate)}
              </span>
            </h4>

            {slotsLoading ? (
              <div className="grid grid-cols-3 gap-2">
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i} className="h-10 bg-gray-100 animate-pulse rounded-md" />
                ))}
              </div>
            ) : slots.length > 0 ? (
              <div className="grid grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
                {slots.map((slot) => (
                  <Button
                    key={slot.id}
                    variant={selectedSlot?.id === slot.id ? "default" : "outline"}
                    size="sm"
                    className={`justify-center text-sm h-10 font-medium ${
                      selectedSlot?.id === slot.id
                        ? "bg-primary text-white border-primary shadow-sm"
                        : "border-gray-200 text-gray-700 hover:border-primary/50 hover:text-primary"
                    }`}
                    onClick={() => onSlotSelect(slot)}
                  >
                    {formatSlotTime(slot)}
                  </Button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-20" />
                <p className="text-sm font-medium">No slots available</p>
                <p className="text-xs mt-1">Try selecting another date</p>
              </div>
            )}
          </div>
        )}

        <div className="pt-4 border-t border-gray-100">
          <Button
            className="w-full h-12 text-base font-bold shadow-lg shadow-primary/10 bg-primary text-white border-0"
            onClick={onBookNow}
            disabled={!selectedSlot}
          >
            {selectedSlot ? "Confirm Appointment" : "Select a Time Slot"}
            {selectedSlot && <ChevronRight className="ml-2 h-5 w-5" />}
          </Button>
          <p className="text-center text-xs text-gray-400 mt-3">
            By booking, you agree to our Terms of Service
          </p>
        </div>
      </div>
    </Card>
  );
}
